import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Badge } from '../ui/badge';
import { Checkbox } from '../ui/checkbox';
import { Plus, RotateCcw, Save, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../contexts/AuthContext';

// Settings → SMS Templates (Collections Center, 10/12). The three starters are
// placeholder wording until Josh pastes his texts; a deleted default resets to
// the shipped wording. Figures come from the deal at send time via {placeholders}.

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SmsTemplatesSettings = () => {
  const { getAuthHeaders } = useAuth();
  const [data, setData] = useState(null);
  const [edits, setEdits] = useState({});
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/collections/templates`, { headers: getAuthHeaders() });
      setData(r.data);
      setEdits(Object.fromEntries(r.data.templates.map(t => [t.id, { ...t }])));
    } catch (e) { toast.error(e.response?.data?.detail || 'Could not load SMS templates'); }
  }, [getAuthHeaders]);
  useEffect(() => { load(); }, [load]);

  const save = async (id) => {
    const t = edits[id];
    setBusy(true);
    try {
      await axios.put(`${API}/collections/templates/${id}`, { name: t.name, body: t.body, kind: t.kind || 'general', active: t.active !== false, codes: (t.codes_text ?? (t.codes || []).join(', ')).split(',').map(x => x.trim()).filter(Boolean) }, { headers: getAuthHeaders() });
      toast.success('Template saved'); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Save failed'); }
    finally { setBusy(false); }
  };
  const remove = async (id, isDefault) => {
    if (!window.confirm(isDefault ? 'Reset this template to the shipped wording?' : 'Delete this template?')) return;
    try {
      await axios.delete(`${API}/collections/templates/${id}`, { headers: getAuthHeaders() });
      toast.success(isDefault ? 'Reset to default' : 'Deleted'); await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };
  const add = async () => {
    try {
      await axios.post(`${API}/collections/templates`, { name: 'New template', body: 'Hi {merchant_name}, ', kind: 'general', active: true }, { headers: getAuthHeaders() });
      await load();
    } catch (e) { toast.error(e.response?.data?.detail || 'Failed'); }
  };

  if (!data) return <div className="text-sm text-muted-foreground">Loading…</div>;
  const defaultIds = new Set((data.defaults || []).map(d => d.id));

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>SMS Templates</CardTitle>
          <CardDescription>Used by the Collections composer. Placeholders in braces are filled from the deal, its contact and the case when you pick the template; unknown placeholders stay visible so a typo is caught.</CardDescription>
        </CardHeader>
        <CardContent className="flex flex-wrap gap-1">
          {Object.entries(data.placeholders || {}).map(([k, v]) => <Badge key={k} variant="outline" title={v}>{`{${k}}`}</Badge>)}
        </CardContent>
      </Card>
      {data.templates.map(t => {
        const e = edits[t.id] || t;
        const isDefault = defaultIds.has(t.id);
        return (
          <Card key={t.id}>
            <CardContent className="pt-4 space-y-2">
              <div className="flex gap-2 items-center">
                <Input className="max-w-sm" value={e.name || ''} onChange={ev => setEdits(p => ({ ...p, [t.id]: { ...e, name: ev.target.value } }))} />
                <Input className="w-32" value={e.kind || ''} placeholder="kind" onChange={ev => setEdits(p => ({ ...p, [t.id]: { ...e, kind: ev.target.value } }))} />
                <Input className="w-44" value={e.codes_text ?? (e.codes || []).join(', ')} placeholder="Return codes, e.g. R01, R09" title="The composer suggests this template when the last bounce carries one of these codes" onChange={ev => setEdits(p => ({ ...p, [t.id]: { ...e, codes_text: ev.target.value } }))} />
                <label className="flex items-center gap-1 text-sm"><Checkbox checked={e.active !== false} onCheckedChange={v => setEdits(p => ({ ...p, [t.id]: { ...e, active: !!v } }))} /> Active</label>
                <span className="ml-auto text-xs text-muted-foreground">{(e.body || '').length} chars</span>
              </div>
              <Textarea rows={3} value={e.body || ''} onChange={ev => setEdits(p => ({ ...p, [t.id]: { ...e, body: ev.target.value } }))} />
              <div className="flex gap-2 justify-end">
                <Button variant="outline" size="sm" onClick={() => remove(t.id, isDefault)}>
                  {isDefault ? <><RotateCcw className="h-4 w-4 mr-1" /> Reset</> : <><Trash2 className="h-4 w-4 mr-1" /> Delete</>}
                </Button>
                <Button size="sm" disabled={busy} onClick={() => save(t.id)}><Save className="h-4 w-4 mr-1" /> Save</Button>
              </div>
            </CardContent>
          </Card>
        );
      })}
      <Button variant="outline" onClick={add}><Plus className="h-4 w-4 mr-1" /> New template</Button>
    </div>
  );
};

export default SmsTemplatesSettings;
